import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

interface Notification {
  id: number;
  message: string;
  severity: AlertColor;
}

interface NotificationsContextType {
  showNotification: (message: string, severity?: AlertColor) => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

interface NotificationsProviderProps {
  children: ReactNode;
}

export const NotificationsProvider: React.FC<NotificationsProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null);
  const [nextId, setNextId] = useState(0);

  const showNotification = useCallback((message: string, severity: AlertColor = 'info') => {
    const id = nextId;
    setNextId(id + 1);
    const notification: Notification = { id, message, severity };

    setNotifications((prev) => [...prev, notification]);

    if (!currentNotification) {
      setCurrentNotification(notification);
    }
  }, [nextId, currentNotification]);

  const handleClose = () => {
    setCurrentNotification(null);

    // Show next notification after a short delay
    setTimeout(() => {
      setNotifications((prev) => {
        const updated = prev.filter((n) => n.id !== currentNotification?.id);
        if (updated.length > 0) {
          setCurrentNotification(updated[0]);
        }
        return updated;
      });
    }, 200);
  };

  // Auto-show next notification when current is cleared
  React.useEffect(() => {
    if (!currentNotification && notifications.length > 0) {
      setCurrentNotification(notifications[0]);
    }
  }, [currentNotification, notifications]);

  return (
    <NotificationsContext.Provider value={{ showNotification }}>
      {children}
      <Snackbar
        open={!!currentNotification}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleClose} severity={currentNotification?.severity} sx={{ width: '100%' }}>
          {currentNotification?.message}
        </Alert>
      </Snackbar>
    </NotificationsContext.Provider>
  );
};

/**
 * Hook to access notifications
 */
export const useNotifications = (): NotificationsContextType => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationsProvider');
  }
  return context;
};
